'use client'

import type { Maybe } from '@/frontend-types'
import { cx } from 'cva'
import type { Easing } from 'motion/react'
import { AnimatePresence, motion, type Variants } from 'motion/react'
import type { FC, PropsWithChildren } from 'react'
import { useId, useRef, useState } from 'react'
import ContentBlockWrapper from '../molecules/ContentBlockWrapper'
import { FontAwesomeIcon } from './FontAwesomeIcon'

export type AccordionType = {
	id: Maybe<string>
	isFirst?: boolean
	title: string
}

const ACCORDION_EASE = [0.04, 0.62, 0.23, 0.98] satisfies Easing

const variants: Variants = {
	open: {
		opacity: 1,
		height: 'auto',
	},
	collapsed: {
		opacity: 0,
		height: 0,
	},
}

const iconMinusVariants: Variants = {
	show: {
		rotate: -90,
		opacity: 1,
	},
	hide: {
		rotate: 0,
		opacity: 0,
	},
}

const Accordion: FC<PropsWithChildren<AccordionType>> = ({ title, children, isFirst = false }) => {
	const [isOpen, setIsOpen] = useState(false)
	const contentRef = useRef<HTMLDivElement>(null)

	const headerId = useId()
	const contentId = useId()

	return (
		<div className="w-full">
			<ContentBlockWrapper>
				<div
					className={cx(
						'relative col-span-full w-full',
						isFirst && 'border-t-(length:--stroke-xs) border-blue-tint-20',
					)}
				>
					<button
						id={headerId}
						type="button"
						className="flex w-full cursor-pointer items-center justify-between gap-x-lg py-2xl group"
						aria-controls={contentId}
						aria-expanded={isOpen}
						onClick={() => {
							setIsOpen(!isOpen)
						}}
					>
						<h3
							className={cx(
								'styled-duration text-left text-h4 transition-colors',
								isOpen
									? 'text-blue-shade-70'
									: 'text-blue group-hover:text-blue-shade-40 group-active:text-blue-shade-70',
							)}
						>
							{title}
						</h3>
						<div className="tablet-lg:self-center self-start" aria-hidden>
							<div
								className={cx(
									'flex h-7 w-7 items-center justify-center rounded-sm border-(length:--stroke-sm) relative',
									isOpen
										? 'border-blue-shade-70 bg-blue-shade-70 text-white'
										: 'border-blue bg-transparent text-blue group-hover:bg-blue-tint-20 group-hover:text-blue-shade-70 group-active:bg-blue-shade-70 group-active:text-white',
								)}
							>
								<div className="absolute">
									<FontAwesomeIcon
										size="xs"
										icon={['fas', 'minus']}
										className="styled-duration transition-colors"
									/>
								</div>
								<motion.div
									initial="show"
									animate={isOpen ? 'hide' : 'show'}
									variants={iconMinusVariants}
									transition={{ duration: 0.3, ease: 'easeInOut' }}
									className="absolute"
								>
									<FontAwesomeIcon
										size="xs"
										icon={['fas', 'minus']}
										className="styled-duration transition-colors"
									/>
								</motion.div>
							</div>
						</div>
					</button>
				</div>
			</ContentBlockWrapper>
			<div
				id={contentId}
				ref={contentRef}
				className="overflow-hidden "
				aria-labelledby={headerId}
				aria-hidden={!isOpen}
			>
				<AnimatePresence initial={false}>
					{isOpen && (
						<motion.div
							initial="collapsed"
							animate="open"
							exit="collapsed"
							variants={variants}
							transition={{ duration: 0.35, ease: ACCORDION_EASE }}
						>
							<div className="pt-lg pb-6xl">{children}</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
			<ContentBlockWrapper>
				<div className="relative col-span-full">
					<div
						className={cx(
							'absolute right-0 bottom-0 left-0 pointer-events-none transition-all ease-in-out duration-200 z-15',
							isOpen
								? 'bg-blue-shade-70 h-(length:--stroke-md)'
								: 'bg-grey-20 h-(length:--stroke-xs)',
						)}
						aria-hidden="true"
					/>
				</div>
			</ContentBlockWrapper>
		</div>
	)
}

export default Accordion
